import {
  MIN_USD_PRICE,
  PRICE_VALUE_LIMIT,
  TRIMMED_INPUT_REGEX
} from 'constants/constants';
import { EScheduleType, OfferType } from 'constants/enums';
import { segmentsFieldsSchema } from 'views/Offers/components/sections';
import { z } from 'zod';

export const SCHEDULE_TAB_TRIGGERS = [
  { value: EScheduleType.PERMANENT, label: 'Permanent' },
  { value: EScheduleType.SCHEDULED, label: 'Schedule' }
];

const rollingOfferPriceSchema = z.number();

export const ROLLING_OFFER_SCHEMA = z
  .object({
    name: z
      .string()
      .min(1, 'Name is required')
      .refine((value) => TRIMMED_INPUT_REGEX.test(value), {
        message: 'Name should not be spaces'
      }),
    publisherOfferId: z
      .string()
      .min(1, 'External ID is required')
      .refine((value) => TRIMMED_INPUT_REGEX.test(value), {
        message: 'External ID should not be spaces'
      }),
    displayName: z.string().optional().default(''),
    description: z.string().optional().default(''),
    offerUiId: z.string().min(1, 'Offer Design is required'),
    price: rollingOfferPriceSchema,
    priority: z
      .number()
      .min(1, 'Priority must be at least 1')
      .max(10, 'Priority must be at most 10'),
    setAsFree: z.boolean().optional().default(false),
    type: z.nativeEnum(OfferType).default(OfferType.ROLLING_OFFER),
    active: z.boolean().optional().default(true),
    tags: z.array(z.string()).default([]),
    schedule: z
      .object({
        permanent: z.boolean().optional().default(true),
        timeFrames: z.array(z.any()).default([])
      })
      .default({ permanent: true, timeFrames: [] }),
    productsSequence: z.array(z.any()).default([]),
    priceDiscount: z
      .number()
      .min(0, 'Price discount cannot be negative')
      .max(99, 'Price discount cannot be more than 99%')
      .optional()
      .default(0),
    productSale: z
      .number()
      .min(0, 'Sale amount cannot be negative')
      .optional()
      .default(0)
  })
  .merge(segmentsFieldsSchema)
  .refine(
    (data) =>
      data.setAsFree === true ||
      (data.price != null && data.price >= MIN_USD_PRICE),
    {
      path: ['price'],
      message: `Price must be at least ${MIN_USD_PRICE}`
    }
  )
  .refine(
    (data) => {
      if (data.setAsFree) {
        return true;
      }
      if (!data.price) {
        return true;
      }
      return String(data.price).replace('.', '').length <= PRICE_VALUE_LIMIT;
    },
    {
      path: ['price'],
      message: 'Price must have at most 10 digits'
    }
  )
  .refine(
    (data) => {
      if (data.setAsFree || !data.price) {
        return true;
      }
      const decimalPart = String(data.price).split('.')[1];
      return !decimalPart || decimalPart.length <= 2;
    },
    {
      path: ['price'],
      message: 'Price must have at most 2 decimal points'
    }
  );

export type RollingOfferFormFields = z.infer<typeof ROLLING_OFFER_SCHEMA>;
